import CustomerHelper from '@/helpers/customerHelper';
import { BOOKING_ORIGIN } from '@/constants/bookingOrigin';
import { BookingGenderCode } from '@/constants/bookingGender';
import { prebook } from '@/services/medkusApi/publicBookingsService';
import { buildStartDateTimeValue, toLocalDateValue, toLocalTimeValue } from '@/helpers/doctorScheduleTimeHelper';
import { DateHelper } from '@/helpers/dateHelper';
import { collectSlotDurations } from '@/helpers/doctorScheduleSlotHelper';
import { CrudService } from '@/services/crudService';
import { normalizeBaseUrl } from '@/helpers/tenantUrlHelper';
import type {
  PrebookPayload as MedkusPrebookPayload,
  Booking as MedkusBooking,
  Block as MedkusBlock,
} from '@/services/medkusApi/types';

type StartInfo = {
  raw: string | null;
  local: string | null;
  iso: string | null;
  display: string | null;
  time: string | null;
};

type SlotInfo = {
  raw?: { start?: string | null } | null;
  resourceName?: string | null;
  resourceId?: number | string | null;
  durations?: Array<number | string> | null;
  preferedDuration?: number | null;
  priceByDuration?: Record<string, unknown> | null;
} & Record<string, unknown>;

type DoctorEntry = Record<string, unknown> & {
  name?: string | null;
  image?: string | null;
  avatarUrl?: string | null;
  photo?: string | null;
  specialty?: string | null;
  specialties?: Array<string | null> | null;
};

type AddressEntry = Record<string, unknown> & {
  id?: number | string | null;
  name?: string | null;
};

type BookingOption = Record<string, unknown> & {
  bookingTypeId?: number | string | null;
  bookingTypeName?: string | null;
  label?: string | null;
  durationMinutes?: number | string | null;
  allowCreateBookByPlace?: boolean | null;
  bookingByPlace?: boolean | null;
  bookingTypeDescription?: string | null;
  description?: string | null;
  price?: string | null;
  priceDisplay?: string | null;
  priceLocalized?: { grossAmountStr?: string | null } | null;
  resourceId?: number | string | null;
};

type PrebookAppointmentParams = {
  date: string;
  time: string;
  slotInfo?: SlotInfo | null;
  startInfo?: StartInfo | null;
  explicitDuration?: number | null;
  scheduleDuration?: number | null;
  activeBookingOption?: BookingOption | null;
  activeBookingTypeId?: number | string | null;
  activeBookingTypeName?: string | null;
  activeDurationMinutes?: number | null;
  activeResourceId?: number | string | null;
  doctor?: DoctorEntry | null;
  isOnline?: boolean | null;
  addresses?: AddressEntry[] | null;
  activeAddressIndex?: number;
  consultationFeeLabel?: string | number | null;
  tenantUrl?: string | null;
  tenantName?: string | null;
  tenantId?: string | null;
  specialty?: string | null;
  defaultTherapy?: string | null;
  defaultTherapyType?: string | null;
  mainAddress?: string | null;
};

type PrebookAppointmentResult = {
  ok: boolean;
  payload: Record<string, unknown> | null;
  saleModel: Record<string, unknown> | null;
  checkoutContext: Record<string, unknown> | null;
};

type PrebookSaleModel = Record<string, unknown> & {
  totalWithDiscountStr?: string | null;
  totalWithDiscount?: number | null;
  duration?: number | string | null;
  bookingId?: number | string | null;
  cancelCustomerId?: number | string | null;
  preBookingMinutes?: number | null;
  cancelUrl?: string | null;
  prebookExpiresAt?: string | null;
};

type PrebookBlock = MedkusBlock & {
  endDate?: string | null;
  localStartDate?: string | null;
  localEndDate?: string | null;
  bookingTypeId?: number | string | null;
  bookingTypeName?: string | null;
  customerName?: string | null;
  email?: string | null;
  phone?: string | null;
};

function normalizeDurationCandidates(values: Array<string | number | null | undefined>): number[] {
  const unique: number[] = [];
  values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0)
    .forEach((candidate) => {
      if (!unique.includes(candidate)) {
        unique.push(candidate);
      }
    });
  return unique;
}

function resolveBookingTypeId(params: PrebookAppointmentParams): number | string | null {
  const option = params.activeBookingOption;
  if (option && option.bookingTypeId !== undefined && option.bookingTypeId !== null) {
    const normalized = Number(option.bookingTypeId);
    return Number.isFinite(normalized) && normalized !== 0 ? normalized : option.bookingTypeId;
  }
  if (params.activeBookingTypeId !== undefined && params.activeBookingTypeId !== null) {
    const normalized = Number(params.activeBookingTypeId);
    return Number.isFinite(normalized) && normalized !== 0 ? normalized : params.activeBookingTypeId;
  }
  return null;
}

function resolveBookingTypeName(params: PrebookAppointmentParams): string {
  const option = params.activeBookingOption;
  const optionName = option && typeof option.bookingTypeName === 'string' ? option.bookingTypeName : null;
  const optionLabel = option && typeof option.label === 'string' ? option.label : null;
  if (optionName) {
    return optionName;
  }
  if (optionLabel) {
    return optionLabel;
  }
  if (params.activeBookingTypeName) {
    return params.activeBookingTypeName;
  }
  if (params.defaultTherapyType === 'therapy' && params.defaultTherapy) {
    return params.defaultTherapy;
  }
  return params.specialty || '';
}

export async function prebookAppointment(params: PrebookAppointmentParams): Promise<PrebookAppointmentResult | null> {
  if (!params.date || !params.time) {
    return null;
  }

  const startInfo = params.startInfo || buildStartDateTimeValue(params.date, params.time);
  const startInfoLocal = typeof startInfo.local === 'string' ? startInfo.local : null;

  const slotInfo = params.slotInfo || null;
  const slotDurations = collectSlotDurations(slotInfo || null);
  const inferredDuration = params.activeDurationMinutes !== null && params.activeDurationMinutes !== undefined
    ? Number(params.activeDurationMinutes) || params.activeDurationMinutes
    : null;
  const optionDuration = params.activeBookingOption && params.activeBookingOption.durationMinutes !== undefined
    ? Number(params.activeBookingOption.durationMinutes) || params.activeBookingOption.durationMinutes
    : null;
  const durationCandidates = normalizeDurationCandidates([
    params.explicitDuration,
    ...slotDurations,
    params.scheduleDuration,
    inferredDuration,
    optionDuration,
  ]);
  let durationCandidate = durationCandidates.length ? durationCandidates[0] : 30;
  if (!params.scheduleDuration && durationCandidate > 60) {
    durationCandidate = 60;
  }
  const normalizedDuration = Math.max(15, Math.min(Math.round(durationCandidate), 240));

  const bookingTypeId = resolveBookingTypeId(params);
  if (!bookingTypeId) {
    return null;
  }
  const bookingTypeName = resolveBookingTypeName(params);

  const tenantBase = params.tenantUrl ? normalizeBaseUrl(params.tenantUrl) : '';
  const effectiveTenantBase = tenantBase;
  const foreignTenant = effectiveTenantBase !== '';
  const allowLocalCustomerId = !foreignTenant;
  const isOnlineValue = params.isOnline === true ? true : (params.isOnline === false ? false : null);
  const bookingByPlaceFlag = Boolean(params.activeBookingOption
    && (params.activeBookingOption.allowCreateBookByPlace ?? params.activeBookingOption.bookingByPlace ?? false));

  const user = CustomerHelper.getUser();
  if (!user) {
    throw new Error('Usuario no autenticado');
  }

  const normalizeString = (value: unknown): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
    return '';
  };

  const storedUserData = JSON.parse(localStorage.getItem('userData') || '{}') as Record<string, unknown>;
  const customerId = null;

  const storedName = normalizeString(storedUserData.name);
  const storedLastName = normalizeString(storedUserData.lastName);
  const storedEmail = normalizeString(storedUserData.email);
  const storedPhone = normalizeString(storedUserData.phone);

  const userName = normalizeString((user as { name?: unknown }).name);
  const userSurname = normalizeString((user as { surname?: unknown }).surname);
  const userEmail = normalizeString((user as { email?: unknown }).email);
  const userPhone = normalizeString((user as { phone?: unknown }).phone);
  const fullName = normalizeString(user.fullName);

  const customerName = storedName || userName || (fullName ? fullName.split(' ')[0] : '');
  const customerSurname = storedLastName || userSurname || (fullName ? fullName.split(' ').slice(1).join(' ') : '');
  const customerEmail = userEmail || storedEmail || '';
  const customerPhone = storedPhone || userPhone || '';
  const storedGenderRaw = storedUserData.gender ?? user.gender ?? null;
  const customerGender = Number.isFinite(Number(storedGenderRaw))
    ? Number(storedGenderRaw)
    : BookingGenderCode.Others;

  const bookingCustomer: MedkusBooking['customer'] = {
    id: customerId,
    name: customerName,
    surname: customerSurname,
    email: customerEmail,
    phone: customerPhone,
    gender: customerGender,
  };

  const slotStart = slotInfo?.raw?.start ?? null;
  const slotResourceName = slotInfo?.resourceName ?? null;
  const slotResourceId = slotInfo?.resourceId ?? null;

  const resourceId = slotResourceId !== undefined && slotResourceId !== null
    ? (Number(slotResourceId) || slotResourceId)
    : (params.activeResourceId !== null && params.activeResourceId !== undefined
      ? (Number(params.activeResourceId) || params.activeResourceId)
      : null);

  const selectedStart = startInfoLocal || slotStart || startInfo.raw || params.date || startInfo.iso || null;
  const parsedStart = selectedStart ? new Date(selectedStart) : null;
  const formattedStart = DateHelper.toSystemString(parsedStart);
  const blockStart = startInfoLocal || formattedStart || (typeof selectedStart === 'string' ? selectedStart : null);

  const parsedEnd = parsedStart && !Number.isNaN(parsedStart.getTime())
    ? new Date(parsedStart.getTime() + (Number.isFinite(normalizedDuration) ? normalizedDuration : 0) * 60000)
    : null;
  const formattedEnd = DateHelper.toSystemString(parsedEnd);
  const localStart = startInfoLocal || formattedStart || null;
  const blockEndBase = formattedEnd;
  const endLocalDate = blockEndBase ? toLocalDateValue(blockEndBase) : null;
  const endLocalTime = blockEndBase ? toLocalTimeValue(blockEndBase) : null;
  const localEnd = endLocalDate && endLocalTime ? `${endLocalDate}T${endLocalTime}:00` : blockEndBase;
  const blockEnd = localEnd || blockEndBase;
  const start = blockStart;

  const addressEntry = Array.isArray(params.addresses)
    ? params.addresses[params.activeAddressIndex ?? 0]
    : null;

  const customerTypeName = normalizeString((user as { customerTypeName?: unknown }).customerTypeName);

  const booking: MedkusBooking = {
    customerId: allowLocalCustomerId ? customerId : null,
    bookingTypeId,
    bookingTypeName,
    isValidated: false,
    sendCommunications: true,
    origin: BOOKING_ORIGIN.MARKETPLACE,
    bookingByPlace: bookingByPlaceFlag,
    customerTypeName,
    description: params.activeBookingOption && typeof params.activeBookingOption.bookingTypeDescription === 'string'
      ? params.activeBookingOption.bookingTypeDescription
      : (params.activeBookingOption && typeof params.activeBookingOption.description === 'string' ? params.activeBookingOption.description : ''),
    observations: '',
    customer: bookingCustomer,
  };

  const block: PrebookBlock = {
    resourceId,
    startDate: blockStart,
    endDate: blockEnd,
    localStartDate: localStart,
    localEndDate: localEnd,
    duration: normalizedDuration,
    allDay: false,
    bookingBy: null,
    resourceName: slotResourceName
      || (addressEntry && typeof addressEntry.name === 'string' ? addressEntry.name : null)
      || params.mainAddress
      || '',
    selected: null,
    resources: [],
    bookingTypeId,
    bookingTypeName,
    customerId: allowLocalCustomerId ? customerId : null,
    customerName: `${customerName} ${customerSurname}`.trim(),
    email: customerEmail,
    phone: customerPhone,
  };

  const prebookPayload: MedkusPrebookPayload = {
    booking,
    customerIsParticipant: true,
    blocks: [block],
    isMultiple: false,
    customerName,
    customerLastName: customerSurname,
    customerPhone,
  };

  const headers: Record<string, string> = {
    Accept: '*/*',
    'Content-Type': 'application/json-patch+json',
  };

  let outboundPayload = prebookPayload;
  if (foreignTenant) {
    outboundPayload = JSON.parse(JSON.stringify(prebookPayload)) as MedkusPrebookPayload;
    outboundPayload.booking.customerId = null;
    const bookingPayload = outboundPayload.booking as Partial<MedkusBooking>;
    if (bookingPayload.customer) {
      delete bookingPayload.customer;
    }
    outboundPayload.blocks = outboundPayload.blocks.map((blockItem) => ({
      ...blockItem,
      customerId: null,
    }));
  }

  let response;
  if (effectiveTenantBase) {
    const tenantCrud = new CrudService({
      baseUrl: effectiveTenantBase,
      allowAuth: true,
      handleUnauthorized: false,
    });
    response = await tenantCrud.httpPost('/api/publicbookings/prebook', outboundPayload, undefined, 'json', {
      headers,
      handleUnauthorized: false,
      baseUrl: effectiveTenantBase,
    });
  } else {
    response = await prebook(outboundPayload);
  }

  const responseData = response && typeof response === 'object' && 'data' in response
    ? (response as { data?: unknown }).data
    : response;
  const payload = responseData && typeof responseData === 'object'
    ? (responseData as Record<string, unknown>)
    : null;
  if (!payload || (payload as { ok?: unknown }).ok === false) {
    return {
      ok: false,
      payload,
      saleModel: null,
      checkoutContext: null,
    };
  }

  const optionPriceLabel = params.activeBookingOption?.priceLocalized?.grossAmountStr
    ?? (typeof params.activeBookingOption?.priceDisplay === 'string'
      ? params.activeBookingOption.priceDisplay
      : null)
    ?? (typeof params.activeBookingOption?.price === 'string'
      ? params.activeBookingOption.price
      : null);

  const payloadModel = (payload as { model?: unknown }).model;
  const salePayload = payloadModel && typeof payloadModel === 'object'
    ? (payloadModel as PrebookSaleModel)
    : null;

  const priceLabel = salePayload?.totalWithDiscountStr
    ? salePayload.totalWithDiscountStr
    : (typeof salePayload?.totalWithDiscount === 'number'
      ? `${salePayload.totalWithDiscount} €`
      : (optionPriceLabel || (params.consultationFeeLabel !== undefined && params.consultationFeeLabel !== null
        ? String(params.consultationFeeLabel)
        : null)));

  const resultDuration = salePayload?.duration ? salePayload.duration : normalizedDuration;
  const durationLabel = resultDuration ? `${resultDuration} min` : null;

  const doctor = params.doctor;
  const therapistSnapshot = doctor
    ? {
        ...doctor,
        image: doctor.image || doctor.avatarUrl || doctor.photo || null,
        avatarUrl: doctor.avatarUrl || doctor.image || doctor.photo || null,
        specialty: doctor.specialty || (Array.isArray(doctor.specialties) ? doctor.specialties[0] : null) || null,
      }
    : null;

  const saleModel: PrebookSaleModel | null = salePayload
    ? {
        ...salePayload,
        tenantUrl: effectiveTenantBase || null,
        tenantName: params.tenantName || params.mainAddress || '',
        cancelUrl: null,
        cancelCustomerId: 0,
        priceLabel,
        durationLabel,
      }
    : null;

  if (saleModel) {
    saleModel.therapist = therapistSnapshot;
    saleModel.specialistName = doctor?.name ?? '';
    saleModel.serviceName = params.activeBookingOption?.bookingTypeName
      || params.specialty
      || params.defaultTherapy
      || '';
    saleModel.startDisplay = startInfo.display || null;
    saleModel.durationLabel = durationLabel;
    saleModel.reservationType = 'appointment';
    if (isOnlineValue !== null) {
      saleModel.isOnline = isOnlineValue;
    }
  }

  if (saleModel?.bookingId && saleModel.cancelCustomerId !== null && saleModel.cancelCustomerId !== undefined) {
    const cancelBase = effectiveTenantBase || '';
    saleModel.cancelUrl = cancelBase
      ? `${cancelBase}/api/publicbookings/cancel/${saleModel.bookingId}/${saleModel.cancelCustomerId}`
      : `/api/publicbookings/cancel/${saleModel.bookingId}/${saleModel.cancelCustomerId}`;
  }

  if (saleModel && typeof saleModel.preBookingMinutes === 'number' && saleModel.preBookingMinutes > 0) {
    const expires = new Date();
    expires.setMinutes(expires.getMinutes() + saleModel.preBookingMinutes);
    saleModel.prebookExpiresAt = DateHelper.toSystemString(expires);
  }

  const checkoutContext = {
    type: 'therapy',
    tenantId: params.tenantId ?? null,
    tenantName: params.tenantName || params.mainAddress || '',
    tenantUrl: effectiveTenantBase || null,
    specialistName: doctor?.name ?? '',
    therapist: therapistSnapshot,
    serviceName: params.activeBookingOption?.bookingTypeName || params.specialty || params.defaultTherapy || '',
    start,
    startDisplay: startInfo.display || null,
    durationMinutes: resultDuration ?? null,
    durationLabel,
    priceLabel,
    isOnline: isOnlineValue,
    slot: {
      date: startInfoLocal || startInfo.raw || params.date,
      hour: startInfo.time,
      resourceName: slotResourceName || (doctor?.name ?? null),
    },
    addressId: addressEntry && addressEntry.id !== undefined ? addressEntry.id : null,
    sale: saleModel,
    rawResponse: payload,
  };

  return {
    ok: true,
    payload,
    saleModel,
    checkoutContext,
  };
}
